import React, { useEffect, useState } from "react";

/**
 * Lightweight global toast stack for license notices (read-only grace, a
 * feature not included in the license, or a cap limit hit). An axios 402
 * interceptor dispatches a window event with a message; we show each as an
 * auto-dismissing toast. There is no app-wide toast system, so this is
 * self-contained and mounted once at the app root.
 *
 * `eventName` lets the customer portal reuse this on its own channel
 * (`portal-license:notice`) without sharing the tech UI's `license:notice`.
 */

type Notice = { id: number; message: string };

let _seq = 0;

export default function LicenseNotices({ eventName = "license:notice" }: { eventName?: string }) {
  const [items, setItems] = useState<Notice[]>([]);

  useEffect(() => {
    const onNotice = (e: Event) => {
      const message = (e as CustomEvent).detail?.message as string | undefined;
      if (!message) return;
      setItems((prev) => {
        // De-dupe: don't stack an identical message that's already showing
        // (a burst of blocked requests would otherwise spam the same toast).
        if (prev.some((n) => n.message === message)) return prev;
        const id = ++_seq;
        window.setTimeout(() => {
          setItems((cur) => cur.filter((n) => n.id !== id));
        }, 6000);
        return [...prev, { id, message }];
      });
    };
    window.addEventListener(eventName, onNotice);
    return () => window.removeEventListener(eventName, onNotice);
  }, [eventName]);

  if (items.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm" role="status" aria-live="polite">
      {items.map((n) => (
        <div
          key={n.id}
          className="flex items-start gap-2 bg-amber-600 text-white px-4 py-2.5 rounded-lg shadow-lg text-sm"
        >
          <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <span>{n.message}</span>
        </div>
      ))}
    </div>
  );
}
